import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MapPin, Users, Clock, Navigation, ChevronLeft, AlertCircle, Loader2, Signal } from 'lucide-react';
import { getInitials } from '../lib/utils';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// --- Marker Factories ---
const createInitialsIcon = (initials, bgColor = '#4f46e5') => new L.DivIcon({
  className: '',
  html: `<div style="
    width:36px;height:36px;border-radius:50%;
    background:${bgColor};border:3px solid white;
    box-shadow:0 2px 10px rgba(0,0,0,0.25);
    display:flex;align-items:center;justify-content:center;
    font-size:11px;font-weight:700;color:white;
    font-family:-apple-system,BlinkMacSystemFont,sans-serif;
  ">${initials}</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

const createTrailIcon = (type, index) => {
  if (type === 'start') {
    return new L.DivIcon({
      className: '',
      html: `<div style="
        width:30px;height:30px;border-radius:50%;
        background:#16a34a;border:3px solid white;
        box-shadow:0 2px 8px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
      "><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21"/></svg></div>`,
      iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
    });
  }
  if (type === 'stop') {
    return new L.DivIcon({
      className: '',
      html: `<div style="
        width:30px;height:30px;border-radius:50%;
        background:#dc2626;border:3px solid white;
        box-shadow:0 2px 8px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
      "><svg width="12" height="12" viewBox="0 0 24 24" fill="white"><rect x="4" y="4" width="16" height="16" rx="2"/></svg></div>`,
      iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
    });
  }
  // Periodic — small numbered dot
  return new L.DivIcon({
    className: '',
    html: `<div style="
      width:22px;height:22px;border-radius:50%;
      background:#3b82f6;border:2px solid white;
      box-shadow:0 1px 6px rgba(0,0,0,0.2);
      display:flex;align-items:center;justify-content:center;
      font-size:9px;font-weight:700;color:white;
      font-family:-apple-system,BlinkMacSystemFont,sans-serif;
    ">${index}</div>`,
    iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -22],
  });
};

// Recenter map helper
const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || 13);
  }, [center, zoom, map]);
  return null;
};

const formatTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function TeamLocations() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTrail, setUserTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === 'employee') navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const fetchTeam = async () => {
      if (!api) return;
      setLoading(true);
      try {
        const res = await api.get(`/locations/team-overview?date=${selectedDate}`);
        setTeamData(res.data);
      } catch (err) {
        console.error('Failed to fetch team locations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [api, selectedDate]);

  useEffect(() => {
    if (!selectedUser || !api) return;
    const fetchTrail = async () => {
      setTrailLoading(true);
      try {
        const res = await api.get(`/locations/user/${selectedUser.id}?date=${selectedDate}`);
        setUserTrail(res.data);
      } catch (err) {
        console.error('Failed to fetch user trail:', err);
      } finally {
        setTrailLoading(false);
      }
    };
    fetchTrail();
  }, [selectedUser, api, selectedDate]);

  // Sort: tracked members first, then alphabetical
  const sortedMembers = useMemo(() => {
    if (!teamData?.members) return [];
    return [...teamData.members].sort((a, b) => {
      const aHas = a.latest_location ? 1 : 0;
      const bHas = b.latest_location ? 1 : 0;
      if (bHas !== aHas) return bHas - aHas;
      return (a.user.name || '').localeCompare(b.user.name || '');
    });
  }, [teamData]);

  const membersWithLocation = useMemo(() => {
    if (!teamData?.members) return [];
    return teamData.members.filter(m => m.latest_location);
  }, [teamData]);

  const mapCenter = useMemo(() => {
    if (selectedUser && userTrail?.locations?.length > 0) {
      const last = userTrail.locations[userTrail.locations.length - 1];
      return [last.latitude, last.longitude];
    }
    if (membersWithLocation.length > 0) {
      const first = membersWithLocation[0].latest_location;
      return [first.latitude, first.longitude];
    }
    return [20.5937, 78.9629];
  }, [membersWithLocation, selectedUser, userTrail]);

  const trailPath = useMemo(() => {
    if (!userTrail?.locations?.length) return [];
    return userTrail.locations.map(l => [l.latitude, l.longitude]);
  }, [userTrail]);

  const handleBackToOverview = () => {
    setSelectedUser(null);
    setUserTrail(null);
  };

  const trackedCount = membersWithLocation.length;
  const totalCount = teamData?.members?.length || 0;

  return (
    <Layout>
      <div className="space-y-4" data-testid="team-locations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {selectedUser && (
              <Button variant="ghost" size="sm" onClick={handleBackToOverview} className="rounded-xl" data-testid="back-to-overview-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-indigo-600" />
                {selectedUser ? `${selectedUser.name}'s Trail` : 'Team Locations'}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {selectedUser ? `Location trail for ${selectedDate}` : 'Real-time location overview of your team'}
              </p>
            </div>
          </div>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44 rounded-xl"
            data-testid="location-date-picker"
          />
        </div>

        {/* Main Content — taller map */}
        <div className="flex gap-4" style={{ height: 'calc(100vh - 200px)', minHeight: 480 }}>
          {/* Member List */}
          <div className="w-72 flex-shrink-0 flex flex-col gap-3">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 flex-1 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Team
                </h3>
                {teamData && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    {trackedCount}/{totalCount}
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : sortedMembers.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-400">No team members found</div>
                ) : (
                  sortedMembers.map((member) => {
                    const hasLocation = !!member.latest_location;
                    const isSelected = selectedUser?.id === member.user.id;
                    return (
                      <button
                        key={member.user.id}
                        onClick={() => hasLocation && setSelectedUser(member.user)}
                        disabled={!hasLocation}
                        className={`w-full px-3 py-2.5 flex items-center gap-2.5 text-left transition-all border-b border-slate-50 dark:border-slate-800/50 ${
                          isSelected
                            ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-2 border-l-indigo-500'
                            : hasLocation
                              ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer'
                              : 'opacity-40 cursor-default'
                        }`}
                        data-testid={`member-${member.user.id}`}
                      >
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          {member.user.profile_photo && <AvatarImage src={member.user.profile_photo} alt={member.user.name} />}
                          <AvatarFallback className="text-xs font-semibold bg-slate-100 dark:bg-slate-700">{getInitials(member.user.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{member.user.name}</p>
                          {hasLocation ? (
                            <>
                              <p className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(member.latest_location.captured_at)}
                                <span className="mx-0.5 text-slate-300">·</span>
                                {member.total_points} pts
                              </p>
                              {member.latest_location.address && (
                                <p
                                  className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5"
                                  title={member.latest_location.address}
                                  data-testid={`member-${member.user.id}-address`}
                                >
                                  {member.latest_location.address}
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-slate-400 italic">No location data</p>
                          )}
                        </div>
                        {hasLocation && (
                          <Signal className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Trail legend (only when viewing a user trail) */}
            {selectedUser && userTrail?.locations?.length > 0 && (
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 px-4 py-3">
                <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Trail Legend</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21"/></svg>
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400">Start</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-[8px] font-bold text-white">#</span>
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400">Checkpoint</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400">Stop</span>
                  </div>
                  <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-xs text-slate-400">{userTrail.locations.length} points recorded</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {!loading && membersWithLocation.length === 0 && !selectedUser ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <AlertCircle className="w-12 h-12 mb-3 text-slate-300" />
                <p className="text-lg font-medium">No location data for {selectedDate}</p>
                <p className="text-sm mt-1 text-center px-8">Location data appears when team members track time with location enabled in the desktop app.</p>
              </div>
            ) : (
              <MapContainer
                center={mapCenter}
                zoom={selectedUser ? 14 : 5}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                zoomControl={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                />
                <RecenterMap center={mapCenter} zoom={selectedUser ? 14 : 5} />

                {/* Team overview — initials markers */}
                {!selectedUser && membersWithLocation.map((member) => (
                  <Marker
                    key={member.user.id}
                    position={[member.latest_location.latitude, member.latest_location.longitude]}
                    icon={createInitialsIcon(getInitials(member.user.name), '#4f46e5')}
                    eventHandlers={{ click: () => setSelectedUser(member.user) }}
                  >
                    <Popup>
                      <div className="text-center min-w-[160px]" data-testid={`team-popup-${member.user.id}`}>
                        <p className="font-semibold text-sm">{member.user.name}</p>
                        <p className="text-xs text-slate-500 capitalize">{member.user.role}</p>
                        {member.latest_location.address && (
                          <p className="text-xs text-slate-700 dark:text-slate-300 mt-1.5 leading-snug">
                            📍 {member.latest_location.address}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">Last: {formatTime(member.latest_location.captured_at)}</p>
                        <p className="text-xs text-slate-400">{member.total_points} location pts today</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* User trail markers */}
                {selectedUser && userTrail?.locations?.map((loc, idx) => (
                  <Marker
                    key={loc.id || idx}
                    position={[loc.latitude, loc.longitude]}
                    icon={createTrailIcon(loc.capture_type, idx + 1)}
                  >
                    <Popup>
                      <div className="text-center min-w-[160px]" data-testid={`trail-popup-${idx}`}>
                        <p className="font-semibold text-xs capitalize">{loc.capture_type === 'periodic' ? `Checkpoint #${idx + 1}` : loc.capture_type}</p>
                        <p className="text-xs text-slate-500">{formatTime(loc.captured_at)}</p>
                        {loc.address && (
                          <p className="text-xs text-slate-700 dark:text-slate-300 mt-1.5 leading-snug">
                            📍 {loc.address}
                          </p>
                        )}
                        {loc.accuracy && <p className="text-xs text-slate-400 mt-1">Accuracy: {Math.round(loc.accuracy)}m</p>}
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* Trail line */}
                {selectedUser && trailPath.length > 1 && (
                  <Polyline
                    positions={trailPath}
                    pathOptions={{ color: '#6366f1', weight: 3, opacity: 0.6, dashArray: '8 4' }}
                  />
                )}
              </MapContainer>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
